/**
 * StaffLeavesCard — rendered inside the Staff Schedule tab.
 *
 * Per-audiologist leave surface. Owner / admin / front desk can mark a
 * date range as leave; other audiologists remain bookable on the same
 * dates. Booking API returns 409 if a slot on a flagged date is
 * attempted for this staff member.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { UserX, Plus, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const todayISO = () => new Date().toISOString().slice(0, 10);
const prettyDate = (iso) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

export default function StaffLeavesCard({ staffId, staffName, canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!staffId) { setRows([]); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/staff-leaves`, {
        params: { user_id: staffId, upcoming_only: true },
      });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [staffId]);
  useEffect(() => { load(); }, [load]);

  // When the audiologist changes, wipe the draft so we don't accidentally
  // stamp last person's dates on the new one.
  useEffect(() => {
    setFrom('');
    setTo('');
    setNote('');
    setErr(null);
  }, [staffId]);

  const add = async () => {
    if (!staffId) { setErr('Pick an audiologist first.'); return; }
    if (!from) { setErr('Pick a start date.'); return; }
    const effectiveTo = to || from;
    if (effectiveTo < from) { setErr('End date must be on or after start date.'); return; }
    setBusy(true);
    setErr(null);
    try {
      await axios.post(`${API}/staff-leaves`, {
        user_id: staffId,
        date_from: from,
        date_to: effectiveTo,
        note: note.trim() || null,
      });
      setFrom('');
      setTo('');
      setNote('');
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not add leave');
    } finally { setBusy(false); }
  };

  const remove = async (leaveId) => {
    setBusy(true);
    setErr(null);
    try {
      await axios.delete(`${API}/staff-leaves/${leaveId}`);
      setRows((prev) => prev.filter((l) => l.leave_id !== leaveId));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not delete');
    } finally { setBusy(false); }
  };

  if (!staffId) return null;

  return (
    <section
      className="bg-white border border-slate-200 rounded-lg p-4"
      data-testid="staff-leaves-card"
    >
      <header className="flex items-center gap-2 mb-1">
        <UserX size={16} className="text-rose-600" />
        <h3 className="text-[13px] font-bold text-slate-900">
          Leave — {staffName || 'audiologist'}
        </h3>
      </header>
      <p className="text-[11.5px] text-slate-500 mb-3">
        Mark a date or a date range when this audiologist is off. Booking
        is blocked on those days for this staff member only — other
        audiologists remain bookable.
      </p>

      {canEdit && (
        <div
          className="grid grid-cols-1 sm:grid-cols-[140px,140px,1fr,auto] gap-2 mb-3"
          data-testid="staff-leaves-add-row"
        >
          <input
            type="date"
            value={from}
            min={todayISO()}
            onChange={(e) => setFrom(e.target.value)}
            placeholder="From"
            data-testid="staff-leaves-from"
            className="border border-slate-300 rounded px-2 py-1.5 text-sm"
          />
          <input
            type="date"
            value={to}
            min={from || todayISO()}
            onChange={(e) => setTo(e.target.value)}
            placeholder="To (optional)"
            data-testid="staff-leaves-to"
            className="border border-slate-300 rounded px-2 py-1.5 text-sm"
          />
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional) — e.g. Vacation, sick day"
            maxLength={240}
            data-testid="staff-leaves-note"
            className="border border-slate-300 rounded px-2 py-1.5 text-sm"
          />
          <button
            onClick={add}
            disabled={busy || !from}
            data-testid="staff-leaves-add-btn"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded"
          >
            <Plus size={13} /> Mark leave
          </button>
        </div>
      )}

      {err && (
        <div
          className="text-[11.5px] px-3 py-1.5 rounded bg-rose-50 border border-rose-200 text-rose-800 mb-3"
          data-testid="staff-leaves-err"
        >
          {typeof err === 'string' ? err : JSON.stringify(err)}
        </div>
      )}

      {loading ? (
        <div className="text-[12px] text-slate-500 italic">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          className="text-[12px] text-slate-500 italic"
          data-testid="staff-leaves-empty"
        >
          No upcoming leave for this audiologist.
        </div>
      ) : (
        <ul
          className="divide-y divide-slate-100 border border-slate-200 rounded"
          data-testid="staff-leaves-list"
        >
          {rows.map((l) => (
            <li
              key={l.leave_id}
              className="flex items-center justify-between gap-2 px-3 py-2"
              data-testid={`staff-leaves-row-${l.leave_id}`}
            >
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold text-slate-800">
                  {l.date_from === l.date_to
                    ? prettyDate(l.date_from)
                    : `${prettyDate(l.date_from)} → ${prettyDate(l.date_to)}`}
                </div>
                {l.note && (
                  <div className="text-[11px] text-slate-500 truncate">
                    {l.note}
                  </div>
                )}
              </div>
              {canEdit && (
                <button
                  onClick={() => remove(l.leave_id)}
                  disabled={busy}
                  data-testid={`staff-leaves-delete-${l.leave_id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:text-rose-800 disabled:opacity-50 px-2 py-1 rounded hover:bg-rose-50"
                  title="Remove leave"
                >
                  <Trash2 size={12} /> Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
